/**
 * Malay message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal anda (locked in l10n/locales.md);
 * anda is lowercase mid-sentence per Malaysian convention (capitalized
 * only sentence-initially), unlike Indonesian's capital "Anda".
 *
 * Malay is NOT Indonesian: core vocabulary diverges deliberately —
 * papan keratan (clipboard), peranti (device), pemberitahuan
 * (notification), tetapan (settings), penyemak imbas (browser),
 * pepijat (bug). Platform terms follow Malaysian Android/Chrome UI
 * conventions (tetapan for settings, tema for theme, perbualan for
 * conversation, gandingan for pairing, ketepikan for dismiss,
 * pencerminan for mirror). "Glance" is the untranslated feature brand
 * name (glossary D1); "klip" renders the D2 clip loanword.
 *
 * Plural notes: Malay is an `other`-only CLDR locale — nouns take no
 * grammatical plural, so every `selectPlural("ms", …)` call supplies only
 * an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Disambungkan",
    statusReconnecting: "Menyambung semula…",
    statusDisconnected: "Terputus",
    openSettings: "Buka tetapan",
    pairedFallbackDevice: "telefon anda",
    publicModeToggleEnable: "Sembunyikan kandungan pemberitahuan",
    publicModeToggleDisable: "Tunjukkan kandungan pemberitahuan",
  },

  notifications: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Hantar",
    sent: "Dihantar",
    failedToSend: "Gagal menghantar",
    unread: "Belum dibaca",
    dismiss: "Ketepikan",
    notificationImageAlt: "Imej pemberitahuan",
  },

  conversations: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Hantar",
    sent: "Dihantar",
    failedToSend: "Gagal menghantar",
    dismiss: "Ketepikan perbualan",
    messagesListLabel: "Mesej dalam perbualan ini",
    deviceOwner: "Anda",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Tiada mesej belum dibaca"
        : `${count} ${selectPlural("ms", count, { other: "mesej belum dibaca" })}`,
  },

  list: {
    today: "Hari ini",
    yesterday: "Semalam",
    searchNotifications: "Cari pemberitahuan",
    clearSearch: "Kosongkan carian",
    clearAll: "Kosongkan semua",
    markAllRead: "Tandai semua dibaca",
    groupCheckboxLabel: "Kumpulkan",
    noNotificationsTitle: "Belum ada pemberitahuan",
    noNotificationsDesc: "Mesej baharu yang dicerminkan dari telefon anda akan muncul di sini.",
    noMatchesTitle: "Tiada padanan",
    notificationsList: "Pemberitahuan",
    privacyAppList: "Pemberitahuan mengikut apl",
    clear: "Kosongkan",
    clearMatches: (count: number): string =>
      `Kosongkan ${count} ${selectPlural("ms", count, { other: "padanan" })}`,
    noMatchesDesc: (query: string): string => `Tiada pemberitahuan sepadan dengan “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Pemberitahuan ${appName}`,
    dismissAllFrom: (appName: string): string => `Ketepikan semua daripada ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ms", count, { other: "mesej" })
          : kind === "calls"
            ? selectPlural("ms", count, { other: "panggilan" })
            : selectPlural("ms", count, { other: "pemberitahuan" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "ketepikan",
    kbdNavigate: "navigasi",
    kbdOrSeparator: "atau",
  },

  settings: {
    back: "Kembali ke pemberitahuan",
    settings: "Tetapan",
    appearance: "Penampilan",
    theme: "Tema",
    themeAuto: "Automatik",
    themeLight: "Cerah",
    themeDark: "Gelap",
    language: "Bahasa",
    languageSystemDefault: "Lalai sistem",
    notificationsSection: "Pemberitahuan",
    osNotifications: "Pemberitahuan OS",
    callNotifications: "Pemberitahuan panggilan",
    privacySection: "Privasi",
    analyticsToggle: "Statistik penggunaan tanpa nama",
    analyticsToggleDescription: "Hanya kiraan dan pencapaian — bukan pemberitahuan anda.",
    connection: "Sambungan",
    status: "Status",
    statusConnected: "Disambungkan",
    statusReconnecting: "Menyambung semula…",
    statusDisconnected: "Terputus",
    disconnectFromPhone: "Putuskan sambungan dari telefon",
    disconnectConfirm: "Anda pasti? Ini akan membatalkan gandingan peranti anda.",
    yesDisconnect: "Ya, putuskan sambungan",
    cancel: "Batal",
    aboutAndSupport: "Perihal & Sokongan",
    version: "Versi",
    encryption: "Penyulitan",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Perlukan bantuan? E-mel __EMAIL__",
    disconnectFailed: "Gagal memutuskan sambungan — sila cuba lagi",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Diputuskan sambungan, tetapi pembersihan setempat gagal: ${errorMsg}. Sila muat semula halaman.`,
    disconnectLocalCleanupFailed:
      "Diputuskan sambungan, tetapi pembersihan setempat gagal. Sila muat semula halaman.",
  },

  pairing: {
    enterCodeInstruction: "Masukkan kod ini dalam apl Reflecto",
    pairedSuccessfully: "Berjaya digandingkan!",
    pairingFailed: "Gandingan gagal",
    requestingPairingCode: "Meminta kod gandingan…",
    codeExpired: "Kod tamat tempoh",
    tryAgain: "Cuba Lagi",
    failedToStartPairing: "Gagal memulakan gandingan",
    needAppText: "Perlukan apl Android? E-mel __EMAIL__ untuk mendapatkan akses.",
  },

  dnd: {
    title: "Jangan Ganggu",
    description: "Jeda pemberitahuan pada peranti ini selama sejam.",
    snooze1h: "Jeda 1 jam",
    snoozeActiveUntil: (formattedTime: string): string => `Aktif sehingga ${formattedTime}`,
    cancelSnooze: "Batal",
  },

  connectors: {
    title: "Perkhidmatan disambungkan",
    emptyBody:
      "Belum ada perkhidmatan yang disambungkan. Sambungkan ChatGPT atau Claude daripada tetapan penyambungnya, kemudian luluskan kod pada telefon anda.",
    scopeSend: "Boleh menghantar pemberitahuan",
    added: (date: string): string => `Ditambah ${date}`,
    lastUsed: (relative: string): string => `Terakhir digunakan ${relative}`,
    neverUsed: "Tidak pernah digunakan",
    revoke: "Putuskan sambungan",
    revokeConfirm: (label: string): string =>
      `Putuskan sambungan ${label}? Ia akan hilang akses serta-merta.`,
    revokeAction: "Putuskan sambungan",
    revokeConfirmLabel: (label: string): string => `Putuskan sambungan ${label}`,
    cancel: "Batal",
    loadError: "Tidak dapat memuatkan perkhidmatan yang disambungkan. Sila cuba lagi.",
    revokeError: "Tidak dapat memutuskan sambungan perkhidmatan. Sila cuba lagi.",
    retry: "Cuba semula",
  },

  feedback: {
    title: "Hantar maklum balas",
    close: "Tutup",
    messageLabel: "Mesej anda",
    messagePlaceholder: "Menemui pepijat, ada idea, atau rasa ada yang tidak kena? Beritahu kami.",
    categoryLabel: "Kategori",
    categoryBug: "Pepijat",
    categoryIdea: "Idea",
    categoryOther: "Lain-lain",
    emailLabel: "E-mel (pilihan, hanya jika anda mahukan balasan)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Kami tidak akan menambah anda ke mana-mana senarai.",
    disclosure:
      "Mesej anda dihantar ke pelayan Reflecto dan direkodkan dalam penjejak isu dalaman kami. Kami melampirkan versi apl anda dan ID peranti yang diberikan semasa gandingan supaya kami dapat mengaitkan laporan. Kami tidak melihat pemberitahuan, kenalan, atau sebarang data peribadi anda. E-mel adalah pilihan — berikannya hanya jika anda mahukan balasan. Kami tidak menambah anda ke mana-mana senarai.",
    submit: "Hantar maklum balas",
    sending: "Menghantar…",
    success: "Terima kasih — dibaca oleh manusia, biasanya dalam masa 24 jam",
    error: "Tidak dapat menghantar. Sila cuba lagi.",
    done: "Selesai",
    sendFeedback: "Hantar maklum balas",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Tutup",
    aiSummaryTag: "Ringkasan AI",
    errorAi: "Tidak dapat menjana ringkasan sekarang.",
    retryCta: "Cuba lagi",
    downloadHeading: "Dapatkan ringkasan AI",
    downloadBody:
      "Kejar apa yang anda terlepas dengan rangkuman AI pada peranti. Berjalan sepenuhnya pada peranti anda.",
    downloadCta: "Muat turun model",
    downloadPrivacyNote:
      "Muat turun sekali sahaja. Pelayan tidak sekali-kali melihat pemberitahuan anda.",
    downloadingHeading: "Memuat turun model…",
    caughtUp: "Anda sudah mengejar semuanya — tiada apa-apa yang memerlukan anda sekarang.",
    needsYou: "Perlukan perhatian",
    quiet: "Lain-lain",
    scanning: (pending: number): string => `Meringkaskan ${pending}…`,
    jump: "Buka pemberitahuan",
    affordanceReply: "Balas",
    affordanceOpen: "Buka",
    affordanceCopyOtp: "Salin kod",
    buttonLabel: "Glance",
  },

  update: {
    title: "Kemas kini tersedia",
    description: "Versi baharu Reflecto sudah sedia. Muat semula untuk mengemas kini.",
    reload: "Muat semula",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Kami menamakan peranti ini “${name}”.`,
    cardLooksGood: "Nampak baik",
    cardEdit: "Edit",
    cardAriaLabel: "Sahkan nama peranti",
    renameTitle: "Namakan semula peranti ini",
    renameLabel: "Nama peranti",
    renameSave: "Simpan",
    renameCancel: "Batal",
    renameFailed: "Tidak dapat menamakan semula. Sila cuba lagi.",
  },

  collision: {
    title: (name: string): string => `Anda sudah mempunyai “${name}”`,
    subtitle: "Apakah yang patut kami namakan yang ini?",
    chipWork: (name: string): string => `Kerja ${name}`,
    chipHome: (name: string): string => `Rumah ${name}`,
    chipPersonal: (name: string): string => `Peribadi ${name}`,
    customLabel: "Nama tersuai",
    save: "Simpan",
    useAnyway: "Guna nama asal juga",
    saveFailed: "Tidak dapat menyimpan nama baharu. Sila cuba lagi.",
    ariaLabel: "Selesaikan nama peranti yang berulang",
  },

  clipboard: {
    title: "Papan keratan",
    empty: "Klip daripada peranti anda yang lain akan muncul di sini. Ketik untuk menyalin.",
    from: (label: string): string => `daripada ${label}`,
    copy: "Salin ke papan keratan",
    copied: "Disalin",
    reveal: "Dedah",
    hide: "Sembunyi",
    maskedHint: "Kandungan disembunyikan — tekan Dedah untuk menunjukkan",
    sensitiveChip: "Sensitif",
    cloudChip: "daripada token API anda",
    sendPlaceholder: "Tampal teks untuk dihantar…",
    sendCta: "Hantar",
    sendSensitiveToggle: "Tandai sebagai sensitif",
    sendSuccess: (n: number): string =>
      `Dihantar ke ${n} ${selectPlural("ms", n, { other: "peranti" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Dicapai ${n}/${total} ${selectPlural("ms", total, { other: "peranti" })}.`,
    sendNoDevices: "Tiada peranti digandingkan untuk dihantar.",
    sendFailed: "Tidak dapat menghantar. Sila cuba lagi.",
    sendEmpty: "Tiada apa-apa untuk dihantar.",
    dockExpand: "Tunjukkan sejarah papan keratan",
    dockCollapse: "Kuncupkan",
    dockSendTitle: "Hantar ke peranti anda",
    dockFromClipboard: (chars: number): string =>
      `Daripada papan keratan · ${chars} ${selectPlural("ms", chars, { other: "aksara" })}`,
    dockSend: (n: number): string => `Hantar ke ${n}`,
    dockCancel: "Batal",
    dockRecentHeading: "Terkini",
    dockEmptyClipboard: "Papan keratan anda kosong.",
    shareTitle: "Hantar ke peranti anda",
    shareReview:
      "Semak apa yang anda hantar. Ia dihantar ke peranti anda yang digandingkan, disulitkan hujung ke hujung.",
    shareSend: "Hantar",
    shareCancel: "Batal",
    shareNotPaired: "Gandingkan peranti dahulu untuk menghantar teks yang dikongsi.",
    shareOpenApp: "Buka Reflecto",
  },
  install: {
    title: "Pasang Reflecto",
    body: "Tambah ke skrin utama anda untuk akses yang lebih pantas.",
    cta: "Pasang",
    dismissAria: "Ketepikan gesaan pemasangan",
  },
  common: {
    loading: "Memuatkan",
  },
  errors: {
    notPaired: "Tidak digandingkan",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} di ${os}`,
    webFallback: "Penyemak imbas web",
  },

  palette: {
    placeholder: "Taip perintah…",
    noResults: "Tiada perintah yang sepadan",
    ariaLabel: "Palet perintah",
    clearAll: "Kosongkan semua pemberitahuan",
    glance: "Ringkasan Glance",
    cancelSnooze: "Batalkan jeda",
    copyClip: "Salin klip terakhir yang diterima",
  },
} satisfies MessagesShape;
